import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { ClarityEvents } from '../../../lib/clarity/types/clarityEvents.types';
import { captureEvent } from '../../../lib/clarity/utils/captureEvent.utils';
import {
  useRegisterSatisfactionSurveyResolution,
  useRegisterSatisfactionSurveyView,
} from '../../../services/modules/satisfactionSurvey/satisfactionSurvey.mutations';
import { useFindSatisfactionSurvey } from '../../../services/modules/satisfactionSurvey/satisfactionSurvey.queries';
import { SatisfactionSurveyResolution } from '../../../services/modules/satisfactionSurvey/satisfactionSurvey.types';

interface UseSatisfactionSurveyGateParams {
  orderId?: string;
  customerId?: string;
  isFinishingOrder: boolean;
  isOfflineMode?: boolean;
  onCancelFinish: () => void;
}

const buildKey = (orderId?: string, customerId?: string) =>
  orderId ? `${orderId}:${customerId ?? 'no-customer'}` : undefined;

const useSatisfactionSurveyGate = ({
  orderId,
  customerId,
  isFinishingOrder,
  isOfflineMode = false,
  onCancelFinish,
}: UseSatisfactionSurveyGateParams) => {
  const { isEnableSatisfactionSurvey } = useFeatureFlag();
  const queryClient = useQueryClient();
  const [resolvedKey, setResolvedKey] = useState<string>();
  const registeredKeyRef = useRef<string>();

  const key = buildKey(orderId, customerId);

  const isQueryEnabled = isEnableSatisfactionSurvey && isFinishingOrder && !isOfflineMode;

  const {
    data: survey,
    isFetching,
    isError,
    queryKey,
  } = useFindSatisfactionSurvey({ orderId, customerId, enabled: isQueryEnabled });

  const isSurveyPending = isQueryEnabled && isFetching;

  const hasResolvedThisSurvey = !!key && resolvedKey === key;

  const shouldShowSurvey =
    isQueryEnabled && !isError && !hasResolvedThisSurvey && !!survey?.shouldShow && !!survey.url;

  const { mutate: registerView } = useRegisterSatisfactionSurveyView();
  const { mutate: registerResolution } = useRegisterSatisfactionSurveyResolution();

  useEffect(() => {
    if (!shouldShowSurvey || !orderId || !key || registeredKeyRef.current === key) {
      return;
    }

    registeredKeyRef.current = key;
    registerView(orderId);
    captureEvent(ClarityEvents.ORDER_SATISFACTION_SURVEY_DISPLAYED);
  }, [shouldShowSurvey, orderId, key, registerView]);

  const resolve = (resolution: SatisfactionSurveyResolution) => {
    if (!orderId || hasResolvedThisSurvey) {
      return;
    }

    registerResolution({ orderId, resolution });
    setResolvedKey(key);
    queryClient.setQueryData(queryKey, { ...survey, shouldShow: false });
  };

  const onAnswerSurvey = () => {
    resolve(SatisfactionSurveyResolution.ANSWERED);
    captureEvent(ClarityEvents.ORDER_SATISFACTION_SURVEY_CLICKED);
  };

  const onContinue = () => resolve(SatisfactionSurveyResolution.CONTINUED);

  const onCancel = () => onCancelFinish();

  return {
    shouldShowSurvey,
    isSurveyPending,
    survey,
    onAnswerSurvey,
    onContinue,
    onCancel,
  };
};

export default useSatisfactionSurveyGate;
